import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import FBIntegrationService from "../services/FBIntegrationService";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "./shared/SuperAdminUi";

function EditFBIntegration() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    appId: "",
    appSecret: "",
    accessToken: "",
    integrationName: "",
    leadGenId: "",
    facebookPageId: "",
    isImportEnabled: true,
    facebookLeadImportFullData: false,
    facebookLeadImportDaysBack: "",
    isActive: false,
    id,
    clientId: "",
    createdBy: "",
    modifiedBy: "",
    createdOn: "",
    modifiedOn: "",
  });
  const [formError, setFormError] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await FBIntegrationService.getFBIntegrationById(id);
        if (!cancelled && row) {
          setForm((prev) => ({
            ...prev,
            ...row,
            isActive: !!row.isActive,
            isImportEnabled: row.isImportEnabled !== false,
            integrationName: row.integrationName ?? row.IntegrationName ?? "",
            facebookPageId: row.facebookPageId ?? row.FacebookPageId ?? "",
            facebookLeadImportFullData: !!row.facebookLeadImportFullData,
            facebookLeadImportDaysBack:
              row.facebookLeadImportDaysBack != null && row.facebookLeadImportDaysBack > 0
                ? String(row.facebookLeadImportDaysBack)
                : "",
            id: row.id ?? id,
          }));
        }
      } catch (error) {
        console.error("Error fetching Facebook integration:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load integration.",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    ["appId", "appSecret", "accessToken", "leadGenId"].forEach((key) => {
      if (!String(form[key] ?? "").trim()) {
        errors[key] = "Required";
      }
    });
    if (!String(form.clientId ?? "").trim()) {
      errors.clientId = "Missing client on this record.";
    }
    const daysRaw = String(form.facebookLeadImportDaysBack ?? "").trim();
    if (!form.facebookLeadImportFullData && daysRaw !== "") {
      const daysParsed = Number.parseInt(daysRaw, 10);
      if (Number.isNaN(daysParsed) || daysParsed < 1) {
        errors.facebookLeadImportDaysBack = "Enter a positive number or leave empty for system default.";
      }
    }
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const buildUpdatePayload = () => {
    const daysRaw = String(form.facebookLeadImportDaysBack ?? "").trim();
    const daysParsed = daysRaw === "" ? null : Number.parseInt(daysRaw, 10);
    return {
      id: Number(form.id ?? id),
      clientId: Number(form.clientId),
      integrationName: String(form.integrationName ?? "").trim(),
      appId: form.appId,
      appSecret: form.appSecret,
      accessToken: form.accessToken,
      leadGenId: form.leadGenId,
      facebookPageId: String(form.facebookPageId ?? "").trim(),
      isImportEnabled: form.isImportEnabled !== false,
      isActive: !!form.isActive,
      facebookLeadImportFullData: !!form.facebookLeadImportFullData,
      facebookLeadImportDaysBack: form.facebookLeadImportFullData ? null : daysParsed,
      createdBy: form.createdBy,
      modifiedBy: form.modifiedBy,
      createdOn: form.createdOn,
      modifiedOn: form.modifiedOn,
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Fill required fields.",
        life: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      await FBIntegrationService.updateFBIntegration(id, buildUpdatePayload());
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Facebook integration updated.",
        life: 2800,
      });
      setTimeout(() => navigate(-1), 700);
    } catch (error) {
      console.error("Error updating integration:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update integration.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const err = (name) =>
    formError[name] ? (
      <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError[name]}</p>
    ) : null;

  const readOnlyStyle = {
    ...saInputSx,
    background: SA.bgSoft,
    color: SA.muted,
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title="Edit Facebook integration"
        subtitle={
          loading ? "Loading…" : `Config #${id} · Client ${form.clientId || "—"}`
        }
        actions={
          <SaGhostButton type="button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
            Back
          </SaGhostButton>
        }
      />

      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fab fa-facebook">Lead Ads credentials</SaSectionTitle>
          <p style={{ margin: "-8px 0 18px", fontSize: "13px", color: SA.muted }}>
            Secrets are masked in list views; update tokens when Meta rotates them. Page ID ties Meta Lead Ads webhooks to this client via one shared callback URL.
          </p>
          <div style={grid}>
            <SaField label="Source name">
              <input name="integrationName" value={form.integrationName ?? ""} onChange={handleInputChange} style={saInputSx} />
            </SaField>
            <SaField label="App ID *">
              <input
                name="appId"
                value={form.appId}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.appId ? SA.danger : SA.border }}
              />
              {err("appId")}
            </SaField>
            <SaField label="App secret *">
              <input
                type="password"
                name="appSecret"
                autoComplete="new-password"
                value={form.appSecret}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.appSecret ? SA.danger : SA.border }}
              />
              {err("appSecret")}
            </SaField>
            <SaField label="Access token *">
              <input
                type="password"
                name="accessToken"
                autoComplete="new-password"
                value={form.accessToken}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.accessToken ? SA.danger : SA.border }}
              />
              {err("accessToken")}
            </SaField>
            <SaField label="Lead Gen form ID *">
              <input
                name="leadGenId"
                value={form.leadGenId}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.leadGenId ? SA.danger : SA.border }}
              />
              {err("leadGenId")}
            </SaField>
            <SaField label="Facebook Page ID (Lead Ads webhook)" hint="Same numeric Page ID Meta sends as entry.id in webhooks.">
              <input
                name="facebookPageId"
                placeholder="Page ID for routing leads into this tenant"
                value={form.facebookPageId ?? ""}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.facebookPageId ? SA.danger : SA.border }}
              />
              {err("facebookPageId")}
            </SaField>
            <SaField label="Client ID" hint="Read-only — tied to this agency.">
              <input name="clientId" value={form.clientId} readOnly style={readOnlyStyle} />
              {err("clientId")}
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-cloud-download-alt">Lead import window</SaSectionTitle>
          <p style={{ margin: "-8px 0 18px", fontSize: "13px", color: SA.muted }}>
            When full history is off, set days back for this client or leave empty to use Scheduler:FacebookLeadImportDaysBack from appsettings.
          </p>
          <div style={grid}>
            <SaField label="Import full history">
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
                <input
                  type="checkbox"
                  name="facebookLeadImportFullData"
                  checked={!!form.facebookLeadImportFullData}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      facebookLeadImportFullData: checked,
                      facebookLeadImportDaysBack: checked ? "" : prev.facebookLeadImportDaysBack,
                    }));
                    setFormError((prev) => ({ ...prev, facebookLeadImportDaysBack: "" }));
                  }}
                  style={{ width: "18px", height: "18px" }}
                />
                Fetch all leads (no date filter)
              </label>
            </SaField>
            <SaField label="Days back (when full history is off)" hint="Empty = system default in appsettings.">
              <input
                name="facebookLeadImportDaysBack"
                type="number"
                min={1}
                disabled={!!form.facebookLeadImportFullData}
                placeholder="e.g. 7"
                value={form.facebookLeadImportDaysBack}
                onChange={handleInputChange}
                style={{
                  ...saInputSx,
                  borderColor: formError.facebookLeadImportDaysBack ? SA.danger : SA.border,
                  opacity: form.facebookLeadImportFullData ? 0.6 : 1,
                }}
              />
              {err("facebookLeadImportDaysBack")}
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-toggle-on">Import &amp; status</SaSectionTitle>
          <SaField label="Scheduled / bulk import">
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
              <input
                type="checkbox"
                name="isImportEnabled"
                checked={form.isImportEnabled !== false}
                onChange={handleInputChange}
                style={{ width: "18px", height: "18px" }}
              />
              Include in scheduler and “import all”
            </label>
          </SaField>
          <SaField label="Active">
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
              <input
                type="checkbox"
                name="isActive"
                checked={!!form.isActive}
                onChange={handleInputChange}
                style={{ width: "18px", height: "18px" }}
              />
              Integration enabled
            </label>
          </SaField>

          <SaFormActions bordered>
            <SaGhostButton type="button" onClick={() => navigate(-1)}>
              Cancel
            </SaGhostButton>
            <SaPrimaryButton type="submit" disabled={saving || loading} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
              {saving ? "Saving…" : "Save changes"}
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      </form>
    </SaPageShell>
  );
}

export default EditFBIntegration;
